import { useRef, useState } from 'react'
import { Download, Upload, FileEarmarkSpreadsheet } from 'react-bootstrap-icons'
import { useData } from '../store.jsx'
import { PageHeader, Panel } from '../components/ui.jsx'
import { updateAt, replaceAll } from '../lib/firebase.js'
import { convertWorkbook } from '../lib/xlsxImport.js'
import { todayISO } from '../lib/format.js'

const CURRENCIES = ['EUR', 'HUF', 'USD', 'GBP']

export default function Settings() {
  const data = useData()
  const fileRef = useRef()
  const xlsxRef = useRef()
  const [msg, setMsg] = useState(null)
  const [pending, setPending] = useState(null)

  function exportJSON() {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `money-backup-${todayISO()}.json`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  async function onFile(e) {
    const file = e.target.files[0]
    e.target.value = ''
    if (!file) return
    try {
      const json = JSON.parse(await file.text())
      const root = json.money && !json.accounts ? json.money : json
      if (typeof root !== 'object' || Array.isArray(root)) throw new Error('Not a money JSON file.')
      setPending({ name: file.name, root })
    } catch (err) { setMsg({ ok: false, text: err.message }) }
  }

  async function onSpreadsheet(e) {
    const file = e.target.files[0]
    e.target.value = ''
    if (!file) return
    try {
      const XLSX = await import('xlsx')
      const wb = XLSX.read(await file.arrayBuffer(), { cellDates: true, cellFormula: true })
      setPending({ name: file.name, root: convertWorkbook(XLSX, wb) })
    } catch (err) { setMsg({ ok: false, text: err.message }) }
  }

  async function confirmImport() {
    // Coinbase data comes from the sync function; keep the current copy instead of the file's.
    const root = { ...pending.root }
    const coinbase = data.integrations?.coinbase
    if (coinbase) root.integrations = { ...root.integrations, coinbase }
    // Never wipe the password vault with a file that doesn't contain one (e.g. the money spreadsheet).
    if (!root.vault && data.vault) root.vault = data.vault
    try {
      await replaceAll(root)
      setMsg({ ok: true, text: `Imported ${pending.name}.` })
    } catch (err) { setMsg({ ok: false, text: err.message }) }
    setPending(null)
  }

  return (
    <>
      <PageHeader title="Settings" />
      <div className="row g-3">
        <div className="col-lg-6">
          <Panel title="Display">
            <label className="form-label small">Currency</label>
            <select className="form-select" value={data.settings.currency} onChange={(e) => updateAt('settings', { currency: e.target.value })}>
              {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <div className="form-text">Display only: amounts are not converted.</div>
          </Panel>
        </div>
        <div className="col-lg-6">
          <Panel title="Backup & import">
            <p className="small text-body-secondary">The whole database is one JSON document. Export it as a backup, import a backup, or start from your <code>money.xlsx</code> (2025+ sheets).</p>
            <div className="d-flex gap-2 flex-wrap">
              <button className="btn btn-outline-primary" onClick={exportJSON}><Download /> Export JSON</button>
              <input ref={fileRef} type="file" accept="application/json,.json" hidden onChange={onFile} />
              <button className="btn btn-outline-danger" onClick={() => fileRef.current.click()}><Upload /> Import JSON…</button>
              <input ref={xlsxRef} type="file" accept=".xlsx" hidden onChange={onSpreadsheet} />
              <button className="btn btn-outline-danger" onClick={() => xlsxRef.current.click()}><FileEarmarkSpreadsheet /> Import spreadsheet…</button>
            </div>
            {pending && (
              <div className="alert alert-warning mt-3 mb-0">
                Importing <b>{pending.name}</b> <b>replaces all current data</b> (the password vault is kept) ({Object.keys(pending.root).filter((k) => Object.keys(pending.root[k] || {}).length).join(', ')}). Export a backup first.
                <div className="mt-2 d-flex gap-2">
                  <button className="btn btn-sm btn-danger" onClick={confirmImport}>Replace everything</button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setPending(null)}>Cancel</button>
                </div>
              </div>
            )}
            {msg && <div className={`alert mt-3 mb-0 py-2 small ${msg.ok ? 'alert-success' : 'alert-danger'}`}>{msg.text}</div>}
          </Panel>
        </div>
      </div>
    </>
  )
}
